import { ClipboardEvent, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import useApi from 'hooks/useApi';
import LegalDocumentView from 'components/legal/LegalDocumentView';
import { errorMessage } from 'components/legal/legalUtils';
import { handleRichPaste } from 'components/chat/chatUtils';
import { LEGAL_DOCUMENT_LABELS, LegalDocument, LegalDocumentType, PublishLegalDocumentPayload } from 'types/legal';

const TITLE_MAX = 120;
const CONTENT_MAX = 100000;

const todayIso = () => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export interface LegalDocumentEditorProps {
  open: boolean;
  onClose: () => void;
  onPublished: () => void;
  type: LegalDocumentType;
  // The client's name.
  contextLabel: string;
  currentVersion?: number | null;
  nextVersion: number;
  // Prefills title and content from the current version, when there is one.
  loadInitial?: () => Promise<LegalDocument | null>;
  publish: (payload: PublishLegalDocumentPayload) => Promise<void>;
}

// Publishing always creates a new, immutable version; nothing here edits one in place.
const LegalDocumentEditor = ({
  open,
  onClose,
  onPublished,
  type,
  contextLabel,
  currentVersion,
  nextVersion,
  loadInitial,
  publish
}: LegalDocumentEditorProps) => {
  const { showToast } = useApi();
  const label = LEGAL_DOCUMENT_LABELS[type];
  const [title, setTitle] = useState(label);
  const [effectiveDate, setEffectiveDate] = useState(todayIso());
  const [content, setContent] = useState('');
  const [initial, setInitial] = useState({ title: label, content: '' });
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const loadRef = useRef(loadInitial);
  loadRef.current = loadInitial;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTitle(label);
    setContent('');
    setInitial({ title: label, content: '' });
    setEffectiveDate(todayIso());
    setError(null);
    setConfirmOpen(false);
    if (!loadRef.current) return;
    setLoadingInitial(true);
    loadRef
      .current()
      .then((doc) => {
        if (cancelled || !doc) return;
        const next = { title: doc.title || label, content: doc.content || '' };
        setTitle(next.title);
        setContent(next.content);
        setInitial(next);
      })
      .catch((err: any) => {
        if (!cancelled) setError(`Could not load the current text: ${errorMessage(err)}`);
      })
      .finally(() => {
        if (!cancelled) setLoadingInitial(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, type, label]);

  const trimmedTitle = title.trim();
  const titleError = !trimmedTitle ? 'Title is required' : trimmedTitle.length > TITLE_MAX ? `At most ${TITLE_MAX} characters` : '';
  const contentError = !content.trim()
    ? 'Content is required'
    : content.length > CONTENT_MAX
    ? `At most ${CONTENT_MAX.toLocaleString()} characters`
    : '';
  const dateError = !effectiveDate || Number.isNaN(new Date(effectiveDate).getTime()) ? 'Pick an effective date' : '';
  const valid = !titleError && !contentError && !dateError;
  const dirty = title !== initial.title || content !== initial.content;

  // Word / Google Docs HTML is sanitised (DOMPurify) and converted to Markdown (Turndown).
  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const markdown = handleRichPaste(event);
    const el = textareaRef.current;
    if (!markdown || !el) return;
    event.preventDefault();
    const start = el.selectionStart ?? content.length;
    const end = el.selectionEnd ?? content.length;
    setContent((prev) => prev.slice(0, start) + markdown + prev.slice(end));
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = start + markdown.length;
    });
  };

  const handleClose = () => {
    if (publishing) return;
    if (dirty && !window.confirm('Discard your changes?')) return;
    onClose();
  };

  const handlePublish = async () => {
    setPublishing(true);
    setError(null);
    try {
      await publish({ title: trimmedTitle, content, effectiveDate });
      showToast(`${label} version ${nextVersion} published`, 'success');
      setConfirmOpen(false);
      onPublished();
      onClose();
    } catch (err: any) {
      const message = errorMessage(err, 'Could not publish this document.');
      setError(message);
      setConfirmOpen(false);
      showToast(message, 'error');
    } finally {
      setPublishing(false);
    }
  };

  const heading = currentVersion ? `Edit ${label}` : `Create ${label}`;

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullScreen aria-labelledby="legal-editor-title">
        <DialogTitle id="legal-editor-title" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }}>
            <Box>
              <Typography variant="h4" component="span" sx={{ display: 'block' }}>
                {heading}
              </Typography>
              <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
                {contextLabel}
                {currentVersion ? ` · currently version ${currentVersion}` : ' · nothing published yet'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" color="secondary" onClick={handleClose} disabled={publishing}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => setConfirmOpen(true)}
                disabled={!valid || loadingInitial || publishing}
                sx={{ bgcolor: 'secondary.dark', '&:hover': { bgcolor: 'secondary.main' } }}
              >
                Publish version {nextVersion}
              </Button>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: '24px !important' }}>
          {loadingInitial ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress aria-label="Loading current text" />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Title"
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      required
                      fullWidth
                      error={Boolean(titleError)}
                      helperText={titleError || `${trimmedTitle.length}/${TITLE_MAX}`}
                    />
                    <TextField
                      label="Effective date"
                      type="date"
                      value={effectiveDate}
                      onChange={(event) => setEffectiveDate(event.target.value)}
                      required
                      error={Boolean(dateError)}
                      helperText={dateError || ' '}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 200 }}
                    />
                  </Stack>
                  <Alert severity="info">
                    Publishing creates version {nextVersion}. People who already accepted are not asked again. English only.
                  </Alert>
                  {error && (
                    <Alert severity="error" role="alert">
                      {error}
                    </Alert>
                  )}
                  <TextField
                    label="Markdown"
                    value={content}
                    onChange={(event) => setContent(event.target.value)}
                    onPaste={handlePaste}
                    inputRef={textareaRef}
                    required
                    multiline
                    minRows={18}
                    maxRows={40}
                    fullWidth
                    error={Boolean(content) && Boolean(contentError)}
                    helperText={
                      (content && contentError) ||
                      `Paste from Word or Google Docs: formatting is converted to Markdown. ${content.length.toLocaleString()}/${CONTENT_MAX.toLocaleString()}`
                    }
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem', alignItems: 'flex-start' } }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Preview
                </Typography>
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 1.5, minHeight: 300 }} aria-label="Preview">
                  {content.trim() ? (
                    <LegalDocumentView content={content} title={trimmedTitle || label} effectiveDate={effectiveDate} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      The rendered document appears here as you type.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={() => !publishing && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="legal-publish-title"
      >
        <DialogTitle id="legal-publish-title">
          Publish version {nextVersion} of the {label}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body1" sx={{ mb: 1 }}>
              It's shown at login from now on.
            </Typography>
            <Typography variant="body1">
              People who already accepted are <strong>not</strong> asked again, and nobody is signed out. Published versions can't be edited
              or deleted.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" color="secondary" onClick={() => setConfirmOpen(false)} disabled={publishing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePublish}
            disabled={publishing}
            startIcon={publishing ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LegalDocumentEditor;
